import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { PosStateService } from './pos-state.service';
import { StorageService, StorageKey } from '../../core/services/storage.service';

/**
 * LOCK-1 — the one POS route that hosts the lock overlay. The overlay is
 * rendered by the POS shell and nowhere else, so while the terminal is locked
 * this is the only authenticated POS screen that may be shown.
 */
export const POS_LOCK_HOST_URL = '/pos/terminal';

/** True when `url` (router state URL, may carry a query/fragment) is the lock host. */
function isLockHost(url: string): boolean {
  return url.split(/[?#;]/, 1)[0] === POS_LOCK_HOST_URL;
}

export const posAuthGuard: CanActivateFn = (_route, state) => {
  const posState = inject(PosStateService);
  const router = inject(Router);
  const storage = inject(StorageService);

  if (posState.isAuthenticated) {
    // POS-ACCESS — defence-in-depth behind the backend PIN-login gate. The
    // terminal requires an EXPLICIT canAccessPos === true (the flag the
    // backend computes from the pos.access permission at PIN-login). Anything
    // else is refused so a user without pos.access can't reach /pos/terminal
    // even via a deep link:
    //   • canAccessPos === false  → kds-only user, send to the KDS.
    //   • canAccessPos undefined  → stale/pre-flag session; we can't prove
    //     access, so don't fall open. Bounce to the KDS as the lower-
    //     privilege surface (its own guard bounces onward if they lack KDS
    //     too); a fresh PIN login re-computes the flag against current perms.
    if (posState.posAuth?.canAccessPos !== true) {
      return router.createUrlTree(['/kds']);
    }

    // LOCK-1 — a locked terminal keeps the previous user's POS auth in memory
    // (lockScreen() locks in place), so "authenticated" alone would let browser
    // Back, a returnUrl or any in-app navigation open their order / settle /
    // report screen with no lock on it. While locked, every guarded POS route
    // resolves to the terminal, where the shell paints the lock overlay.
    // replaceUrl so the refused screen doesn't stay in history one Back away.
    if (posState.isLocked && !isLockHost(state.url)) {
      return new RedirectCommand(router.parseUrl(POS_LOCK_HOST_URL), { replaceUrl: true });
    }
    return true;
  }

  // Not PIN-authenticated — redirect to the correct PIN screen, preserving the
  // attempted URL as returnUrl so the PIN screen can route back here after
  // login (e.g. the edge client's deep-linked enrolment wizard).
  // ✅ FIXED: Use StorageService instead of localStorage
  const standaloneCode = storage.getRaw(StorageKey.POS_STANDALONE_CODE);
  if (standaloneCode) {
    return router.createUrlTree(['/pos/s', standaloneCode], {
      queryParams: { returnUrl: state.url },
    });
  }
  return router.createUrlTree(['/pos'], { queryParams: { returnUrl: state.url } });
};
